import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'
import type { AppDispatch } from '@redux/store'
import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { PaymentMethodModel } from '@models/paymentMethod'
import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/paymentMethod/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/paymentMethod/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/paymentMethod/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/paymentMethod/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/paymentMethod/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/paymentMethod/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/paymentMethod/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/paymentMethod/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/paymentMethod/UPDATE_FAILURE'

export type PaymentMethodModuleState = {
  creating: boolean,
  errors: Errors,
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: PaymentMethodModuleState = {
  creating: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(): Action {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(): Action {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []): Action {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadPaymentMethods(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().paymentMethods.loadedForKeys]

    if (entityKey) loadedForKeys.push(entityKey)

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/payment_methods.json', options)
      .then(({ data }: { data: PaymentMethodModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PAYMENT_METHOD_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadPaymentMethod(paymentMethodId: number | string, options: EntityOptions = {}) {
  const { entityKey } = options || {}

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().paymentMethods.loadedForKeys]

    if (entityKey) loadedForKeys.push(entityKey)

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api(`/payment_methods/${paymentMethodId}.json`, options)
      .then(({ data }: { data: PaymentMethodModel}) => {
        const normalizedJson = normalize(data, Schemas.PAYMENT_METHOD)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createPaymentMethod(paymentMethod: PaymentMethodModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      payment_method: paymentMethod,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/payment_methods.json', options, config)
      .then(({ data }: { data: PaymentMethodModel }) => {
        const normalizedJson = normalize(data, Schemas.PAYMENT_METHOD)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updatePaymentMethod(paymentMethod: PaymentMethodModel, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      payment_method: paymentMethod,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/payment_methods/${paymentMethod.id}.json`, options, config)
      .then(({ data }: { data: PaymentMethodModel }) => {
        const normalizedJson = normalize(data, Schemas.PAYMENT_METHOD)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: PaymentMethodModuleState = initialState,
  action: Action = { type: '' },
): PaymentMethodModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
